// Procedural rock-planet geometry + a "ground sampler" so the character
// controller can compute the surface position/normal for any point in space.
//
// We build the planet from an icosphere displaced by 3-octave value noise.
// To make movement on the surface feel real, we also expose `sampleHeight(dir)`
// which returns the radial distance to the surface for a unit direction.

import * as THREE from 'three';

// Deterministic 3D value noise. Identical seed → identical rock.
function makeNoise(seed) {
  const hash = (x, y, z) => {
    const s = Math.sin(x * 12.9898 + y * 78.233 + z * 37.719 + seed * 9.17) * 43758.5453;
    return s - Math.floor(s);
  };
  return (x, y, z) => {
    let n = 0, amp = 1, freq = 1, norm = 0;
    for (let i = 0; i < 3; i++) {
      const xi = Math.floor(x * freq), yi = Math.floor(y * freq), zi = Math.floor(z * freq);
      const xf = x * freq - xi, yf = y * freq - yi, zf = z * freq - zi;
      const u = xf * xf * (3 - 2 * xf), vv = yf * yf * (3 - 2 * yf), w = zf * zf * (3 - 2 * zf);
      const c000 = hash(xi, yi, zi),     c100 = hash(xi+1, yi, zi);
      const c010 = hash(xi, yi+1, zi),   c110 = hash(xi+1, yi+1, zi);
      const c001 = hash(xi, yi, zi+1),   c101 = hash(xi+1, yi, zi+1);
      const c011 = hash(xi, yi+1, zi+1), c111 = hash(xi+1, yi+1, zi+1);
      const x0 = c000*(1-u) + c100*u, x1 = c010*(1-u) + c110*u;
      const x2 = c001*(1-u) + c101*u, x3 = c011*(1-u) + c111*u;
      const y0 = x0*(1-vv) + x1*vv, y1 = x2*(1-vv) + x3*vv;
      n += (y0*(1-w) + y1*w) * amp;
      norm += amp; amp *= 0.5; freq *= 2.1;
    }
    return n / norm;
  };
}

// height(dir) returns the displacement applied to a unit vector. The planet
// surface at direction `dir` is `dir * (radius + height(dir) * radius)`.
function makeHeightField({ jitter, seed }) {
  const noise = makeNoise(seed);
  return (dir) => {
    const big = noise(dir.x * 1.4, dir.y * 1.4, dir.z * 1.4);
    const small = noise(dir.x * 4.0, dir.y * 4.0, dir.z * 4.0);
    const flatten = Math.max(0, dir.y) * 0.18;
    return (big * 0.7 + small * 0.3) * jitter - flatten * jitter * 0.5;
  };
}

export function buildPlanet({ radius = 22, detail = 5, jitter = 0.32, seed = 13 } = {}) {
  const geo = new THREE.IcosahedronGeometry(radius, detail);
  const heightFn = makeHeightField({ jitter, seed });
  const pos = geo.attributes.position;
  const v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i);
    const dir = v.clone().normalize();
    const d = heightFn(dir);
    v.copy(dir).multiplyScalar(radius + d * radius);
    pos.setXYZ(i, v.x, v.y, v.z);
  }
  geo.computeVertexNormals();

  // Sampler used by character controller and prop placement. Returns the
  // distance from origin to surface along `dir`.
  const sampleRadius = (dir) => radius + heightFn(dir) * radius;

  return { geometry: geo, radius, sampleRadius, heightFn };
}
